import cls from 'classnames';
import { observer } from 'mobx-react-lite';
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useHistory, useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumb, Modal, Form, FloatingLabel, Button, Spinner } from 'react-bootstrap';
import { TooltipControl } from 'src/components/base/Tooltip';
import { WorkspaceBanner } from 'src/components/base/WorkspaceBanner';
import { VerticalSplitter } from 'src/components/base/Splitter';
import { LargeContainer, SimpleHeader } from 'src/components/base/Elements';
import { useFetchTraps, useUpdateTrap } from 'src/model/traps';
import { useFetchGroupMap, useFetchGroupMaps, useCreateOrUpdateGroupMap, useDeleteGroupMap } from 'src/model/images';
import { useCurrentWorkspace } from 'src/model/workspaces';
import { useFetchGroup, useFetchGroups } from 'src/model/groups';
import { useEffectState } from 'src/utils/hooks';
import { isSystemGroup } from '../../utils/helpers';
import { hasPermission, PERMISSION_GROUP_EDIT, PERMISSION_GROUP_EDIT_FLOOR, PERMISSION_TRAP_EDIT, PERMISSION_TRAP_EDIT_MAP } from 'src/api/permissions';
import { useSystemStore } from 'src/stores';
import { ImageFileInput } from 'src/components/base/ImageFileInput';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';

import { BuildingMapsSidePanel } from './BuildingMapsSidePanel';
import { BuildingMapsPanel } from './BuildingMapsPanel';

import classnames from './BuildingMaps.module.scss';

// Custom validation for x/y coordinates (relative 0-1 range) and map placement
const isValidBuildingPosition = ({ x, y, map_id } = {}, currentMapId) => {
  return typeof x === 'number' && typeof y === 'number' &&
         !Number.isNaN(x) && !Number.isNaN(y) &&
         x >= 0 && x <= 1 && y >= 0 && y <= 1 &&
         map_id === currentMapId;
};

const MAX_MAP_PER_GROUP = 10;
const MAX_MAP_IMAGE_SIZE = 10 * 1024 * 1024;

export const BuildingMaps = observer(() => {
  const [center, setCenter] = useState();
  const [minimized, setMinimized] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [selectedItem, setSelectedItem] = useState();
  const history = useHistory();
  const location = useLocation();
  const { workspace: { workspace_id } } = useCurrentWorkspace();
  const { workspaces, workspace: currentWorkspaceDetails, user } = useSystemStore();
  const buildingMapWarningModal = useModalDialog();

  const previousSearchRef = useRef(location.search);
  const userUnselectedRef = useRef(false);

  const params = useParams();
  const group_id = !isNaN(Number.parseInt(params.id)) ? +params.id : undefined;
  const map_id = !isNaN(Number.parseInt(params.map_id)) ? +params.map_id : undefined;

  const { data: group } = useFetchGroup({ group_id });
  const isSystemManagedGroup = isSystemGroup(group);
  const { data: map = {}, mutate: mutateMap } = useFetchGroupMap({ id: group_id, map_id });
  const { data: maps = [], mutate: mutateMaps } = useFetchGroupMaps({ id: group_id });
  const { data, mutate } = useFetchTraps({ workspace_id, group_id, pageSize: 1000 });
  const allGroupsResponse = useFetchGroups({ workspace_id, pageIndex: 0, pageSize: 1000, sortBy: { id: undefined, desc: undefined }, filter: { key: 'type', value: 'user' } });
  const [updateTrapAction] = useUpdateTrap(mutate);
  const [createOrUpdateGroupMapAction] = useCreateOrUpdateGroupMap();
  const [deleteGroupMapAction] = useDeleteGroupMap(mutateMaps);
  const deleteModal = useModalDialog();

  const [isMapModalVisible, setMapModalVisible] = useState(false);
  const [mapFormName, setMapFormName] = useState('');
  const [mapFormError, setMapFormError] = useState('');
  const [mapFormImage, setMapFormImage] = useState(null);
  const [mapImageInputKey, setMapImageInputKey] = useState(0);
  const [isMapActionPending, setMapActionPending] = useState(false);
  const [mapActionType, setMapActionType] = useState(null);
  const [modalMapId, setModalMapId] = useState();

  const isEditingMap = Number.isFinite(modalMapId);
  const mapModalTitle = isEditingMap ? __('Edit Map') : __('New Map');
  const mapPrimaryActionLabel = isEditingMap ? __('Save Changes') : __('Create Map');

  const resolveWorkspacePermissions = useCallback((targetWorkspaceId) => {
    if (Array.isArray(workspaces)) {
      const match = workspaces.find(({ workspace_id: id }) => id === targetWorkspaceId);
      if (match) {
        return match.permissions;
      }
    }

    if (currentWorkspaceDetails?.workspace_id === targetWorkspaceId) {
      return currentWorkspaceDetails.permissions;
    }

    return user?.permissions;
  }, [workspaces, currentWorkspaceDetails, user]);

  const canEditTrap = useCallback((targetWorkspaceId) => {
    const permissions = resolveWorkspacePermissions(targetWorkspaceId);
    return hasPermission(permissions, PERMISSION_TRAP_EDIT) || hasPermission(permissions, PERMISSION_TRAP_EDIT_MAP);
  }, [resolveWorkspacePermissions]);

  const groupWorkspaceId = Number.isFinite(Number(group?.workspace_id)) ? Number(group.workspace_id) : group?.workspace_id ?? workspace_id;

  const [items, setItems] = useEffectState(() => data.items.map(({ trap_id, trap_name, event_priority, is_caught, event_name, serial_no, x, y, map_id: trapMapId, map_group_id, latitude, longitude }, index) => {
    const validX = typeof x === 'number' ? x : null;
    const validY = typeof y === 'number' ? y : null;
    const placement = { x: validX, y: validY, map_id: trapMapId ?? null };
    const hasGoogleCoordinates = typeof latitude === 'number' && !Number.isNaN(latitude) && typeof longitude === 'number' && !Number.isNaN(longitude);
    const normalizedMapGroupId = Number.isFinite(Number(map_group_id)) ? Number(map_group_id) : null;

    return {
      index,
      trap_id,
      trap_name,
      serial_no,
      event_priority,
      is_caught,
      event_name,
      position: placement,
      changed: { ...placement },
      hasGoogleCoordinates,
      workspaceId: groupWorkspaceId,
      mapGroupId: normalizedMapGroupId,
    };
  }), [data.items, map_id, groupWorkspaceId]);

  const hasSelectableMap = Array.isArray(maps) && maps.some(({ id }) => Number(id) === Number(map_id));
  const canEditTrapPositions = Number.isFinite(map_id) && hasSelectableMap && !isSystemManagedGroup;

  const canToggleEditMode = useMemo(() => {
    if (!canEditTrapPositions) {
      return false;
    }
    return items.some(item => canEditTrap(item.workspaceId));
  }, [canEditTrapPositions, items, canEditTrap]);

  const canEditGroupMaps = useMemo(() => {
    const permissions = resolveWorkspacePermissions(groupWorkspaceId);
    return hasPermission(permissions, PERMISSION_GROUP_EDIT) || hasPermission(permissions, PERMISSION_GROUP_EDIT_FLOOR);
  }, [resolveWorkspacePermissions, groupWorkspaceId]);

  const availableGroupIds = useMemo(() => {
    const groupsData = allGroupsResponse.data ?? { items: [] };
    const groupItems = groupsData.items ?? [];
    return new Set(groupItems.map(group => Number(group.group_id)));
  }, [allGroupsResponse.data]);

  const visibleItems = useMemo(() => {
    return items.filter(item => {
      // Check if trap is assigned to any map (current building map, another building map, or Google map)
      const isOnBuildingMap = Number.isFinite(item.position?.map_id);
      const isOnGoogleMap = item.hasGoogleCoordinates === true;
      const isAssignedToAnyMap = isOnBuildingMap || isOnGoogleMap;

      // If trap is on a building map of an inaccessible group AND user cannot edit trap location, hide it
      if (isOnBuildingMap && Number.isFinite(item.mapGroupId)) {
        const groupIsAvailable = availableGroupIds.has(item.mapGroupId);
        const userCanEditTrap = canEditTrap(item.workspaceId);
        if (!groupIsAvailable && !userCanEditTrap) {
          return false;
        }
      }

      // If trap is assigned to any map, always show it (if group/permission check passed)
      if (isAssignedToAnyMap) {
        return true;
      }

      // If trap is not assigned to any map, only show if user can edit trap positions
      return canEditTrap(item.workspaceId);
    });
  }, [items, canEditTrap, availableGroupIds]);

  useEffect(() => {
    if (!canToggleEditMode && editMode) {
      setEditMode(false);
    }
  }, [canToggleEditMode, editMode]);

  const handleEditModeChange = useCallback((value) => {
    if (!canToggleEditMode) { return; }
    setEditMode(value);
  }, [canToggleEditMode]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trapIdParam = params.get('trap_id');
    const trapId = Number(trapIdParam);

    if (!trapIdParam || !Number.isFinite(trapId)) {
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
      return;
    }

    if (!items.length) {
      return;
    }

    const match = items.find(item => Number(item.trap_id) === trapId);
    if (!match) {
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
      return;
    }

    const selectedTrapId = Number(selectedItem?.trap_id);
    if (selectedTrapId === trapId) {
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
      return;
    }

    // Don't auto-select if user manually unselected
    if (userUnselectedRef.current) {
      previousSearchRef.current = location.search;
      return;
    }

    const searchChanged = previousSearchRef.current !== location.search;
    if (searchChanged || !selectedItem) {
      setSelectedItem(match);
      previousSearchRef.current = location.search;
      userUnselectedRef.current = false;
    }
  }, [location.search, items, selectedItem]);

  async function handleDropItem(dropped) {
    if (!canEditTrapPositions) {
      return;
    }

    const previousMapId = Number.isFinite(dropped?.position?.map_id) ? Number(dropped.position.map_id) : null;
    const nextMapId = Number(map_id);
    const isMovingFromAnotherBuildingMap = Number.isFinite(previousMapId) && previousMapId !== nextMapId;
    const isMovingFromGoogleMap = !Number.isFinite(previousMapId) && dropped?.hasGoogleCoordinates;

    if (isMovingFromAnotherBuildingMap || isMovingFromGoogleMap) {
      const warningText = isMovingFromAnotherBuildingMap
        ? __('The trap is placed on another building map. If you place it here then the existing link will be removed.')
        : __('The trap is placed on Google Maps. If you place it on the building map then the Google Maps coordinates will be cleared.');

      const shouldContinue = await buildingMapWarningModal.show({
        text: warningText,
        variant: 'primary',
        actionTitle: __('Proceed')
      });
      if (!shouldContinue) {
        return;
      }
    }

    const fallbackCenter = { x: 0.5, y: 0.5 };
    const basePosition = (center && typeof center?.x === 'number' && typeof center?.y === 'number') ? center : fallbackCenter;
    const targetPosition = { ...basePosition, map_id };

    setItems(prevItems => prevItems.map((item) => (
      item.trap_id === dropped.trap_id
        ? { ...item, changed: targetPosition, hasGoogleCoordinates: isMovingFromGoogleMap ? false : item.hasGoogleCoordinates }
        : item
    )));

    setSelectedItem({ ...dropped, changed: targetPosition, hasGoogleCoordinates: isMovingFromGoogleMap ? false : dropped.hasGoogleCoordinates });

    await updateTrapAction({ trap_id: dropped.trap_id, map_id, x: targetPosition.x, y: targetPosition.y });
  }

  const handleSelectItem = useCallback((item) => {
    if (item === null) {
      // Clear trap_id from URL when manually unselecting
      const params = new URLSearchParams(location.search);
      if (params.has('trap_id')) {
        params.delete('trap_id');
        const newSearch = params.toString();
        history.replace({ ...location, search: newSearch ? `?${newSearch}` : '' });
        userUnselectedRef.current = true;
      }
    }
    setSelectedItem(item);
  }, [history, location]);

  async function handleRemoveFromMap(item) {
    if (!canEditTrapPositions) {
      return;
    }

    setItems(prevItems => prevItems.map((current) => (
      current.trap_id === item.trap_id
        ? { ...current, changed: { x: null, y: null, map_id: null } }
        : current
    )));

    const currentSelected = selectedItem;
    if (currentSelected?.trap_id === item.trap_id) {
      handleSelectItem(null);
    }

    await updateTrapAction({ trap_id: item.trap_id, map_id: '', x: '', y: '' });
  }

  useEffect(() => {
    if (!canEditTrapPositions && editMode) {
      setItems((prevItems) => prevItems.map((item) => ({
        ...item,
        changed: { ...item.position },
      })));
      setEditMode(false);
    }
  }, [canEditTrapPositions, editMode, setItems]);

  useEffect(() => {
    if (!canEditTrapPositions) {
      setSelectedItem(null);
    }
  }, [canEditTrapPositions]);

  const isSpecialGroup = group_id === 0 || group_id === -1;
  const groupName = isSpecialGroup
    ? (group_id === 0 ? __('All Traps') : __('My Traps'))
    : group?.group_name;

  const handleHomeClick = useCallback(() => {
    history.push('/traps');
  }, [history]);

  const handleGroupClick = useCallback(() => {
    if (group_id === undefined) {
      return;
    }

    history.push(`/traps/${group_id}`);
  }, [group_id, history]);

  const handleMapChange = useCallback((nextMapId) => {
    const normalizedId = Number(nextMapId);
    if (!Number.isFinite(normalizedId) || normalizedId === map_id || !group_id) {
      return;
    }

    if (editMode) {
      setItems(prevItems => prevItems.map((item) => ({
        ...item,
        changed: { ...item.position },
      })));
      setEditMode(false);
      setSelectedItem(null);
    }

    history.push(`/groups/${group_id}/maps/${normalizedId}/traps`);
  }, [editMode, group_id, history, map_id]);

  const handleOpenMapModal = useCallback(() => {
    if (!Number.isFinite(map_id) || isSystemManagedGroup) {
      return;
    }

    setModalMapId(map_id);
    setMapFormName(typeof map?.name === 'string' ? map.name : '');
    setMapFormError('');
    setMapFormImage(null);
    setMapImageInputKey((value) => value + 1);
    setMapModalVisible(true);
  }, [isSystemManagedGroup, map, map_id]);

  const handleCloseMapModal = useCallback(() => {
    if (isMapActionPending) {
      return;
    }

    setMapModalVisible(false);
    setModalMapId(undefined);
    setMapFormError('');
    setMapFormImage(null);
    setMapImageInputKey((value) => value + 1);
  }, [isMapActionPending]);

  const handleNewMapClick = useCallback(() => {
    if (!Number.isFinite(group_id) || isSystemManagedGroup) {
      return;
    }
    if (Array.isArray(maps) && maps.length >= MAX_MAP_PER_GROUP) {
      return;
    }

    setModalMapId(undefined);
    setMapFormName('');
    setMapFormError('');
    setMapFormImage(null);
    setMapImageInputKey((value) => value + 1);
    setMapModalVisible(true);
  }, [group_id, isSystemManagedGroup]);

  const handleMapFormNameChange = useCallback((event) => {
    setMapFormName(event.target.value ?? '');
    if (mapFormError) {
      setMapFormError('');
    }
  }, [mapFormError]);

  const handleMapFormImageSelect = useCallback((file) => {
    setMapFormImage(file ?? null);
  }, []);

  const handleSaveMapDetails = useCallback(async (event) => {
    event.preventDefault();

    if (!group_id || isMapActionPending || isSystemManagedGroup) {
      return;
    }

    const targetMapId = Number.isFinite(modalMapId) ? Number(modalMapId) : undefined;
    const trimmedName = (mapFormName ?? '').trim();
    if (!trimmedName) {
      setMapFormError(__('Name is required'));
      return;
    }

    if (trimmedName.length > 40) {
      setMapFormError(__('Name must be 40 characters or less'));
      return;
    }

    setMapActionPending(true);
    setMapActionType('save');

    try {
      const payload = { id: group_id, workspace_id, name: trimmedName };
      if (Number.isFinite(targetMapId)) {
        payload.map_id = targetMapId;
      }
      if (mapFormImage) {
        payload.file = mapFormImage;
      }

      const { error, data: savedMap } = await createOrUpdateGroupMapAction(payload);
      if (error) {
        return;
      }

      let updatedMaps;
      if (typeof mutateMaps === 'function') {
        updatedMaps = await mutateMaps();
      }
      if (Number.isFinite(targetMapId) && typeof mutateMap === 'function') {
        await mutateMap();
      }

      setMapModalVisible(false);
      setModalMapId(undefined);
      setMapFormImage(null);
      setMapImageInputKey((value) => value + 1);
      setMapFormName(trimmedName);
      setMapFormError('');

      if (!Number.isFinite(targetMapId)) {
        const createdId = Number(savedMap?.id ?? (Array.isArray(updatedMaps) ? updatedMaps[updatedMaps.length - 1]?.id : undefined));
        if (Number.isFinite(createdId)) {
          history.replace(`/groups/${group_id}/maps/${createdId}/traps`);
        }
      }
    } finally {
      setMapActionPending(false);
      setMapActionType(null);
    }
  }, [createOrUpdateGroupMapAction, group_id, history, isMapActionPending, isSystemManagedGroup, mapFormImage, mapFormName, modalMapId, mutateMap, mutateMaps, workspace_id]);

  const handleDeleteMap = useCallback(async () => {
    const targetMapId = Number.isFinite(modalMapId) ? Number(modalMapId) : undefined;
    if (!Number.isFinite(targetMapId) || !group_id || isMapActionPending || isSystemManagedGroup) {
      return;
    }

    const mapTitle = typeof map?.name === 'string' ? map.name : '';
    const confirmationText = <>{__('Are you sure you want to delete')} <b>{mapTitle}</b>?</>;

    const shouldDelete = await deleteModal.show({ text: confirmationText });
    if (!shouldDelete) {
      return;
    }

    setMapActionPending(true);
    setMapActionType('delete');

    try {
      const { error } = await deleteGroupMapAction({ id: group_id, map_id: targetMapId });
      if (error) {
        return;
      }

      const updatedMaps = typeof mutateMaps === 'function' ? await mutateMaps() : maps.filter(({ id }) => Number(id) !== targetMapId);

      if (typeof mutateMap === 'function') {
        try {
          await mutateMap();
        } catch (error) {
          console.warn('Failed to refresh map details after deletion', error);
        }
      }

      // Clear map_id, x, and y for all traps that were placed on the deleted map
      const trapsOnDeletedMap = Array.isArray(data?.items)
        ? data.items.filter(({ map_id: trapMapId }) => Number(trapMapId) === targetMapId)
        : [];

      if (trapsOnDeletedMap.length > 0) {
        await Promise.all(
          trapsOnDeletedMap.map(({ trap_id }) =>
            // it is enough to clear data locally (server clears them automatically), but keep it for now
            updateTrapAction({ trap_id, map_id: '', x: '', y: '' })
          )
        );

        // Refresh traps data to update UI immediately
        if (typeof mutate === 'function') {
          await mutate();
        }
      }

      setMapModalVisible(false);
      setModalMapId(undefined);
      setMapFormImage(null);
      setMapImageInputKey((value) => value + 1);
      setMapFormError('');

      const fallback = Array.isArray(updatedMaps) ? updatedMaps.find(({ id }) => Number.isFinite(Number(id))) : undefined;
      const fallbackId = fallback ? Number(fallback.id) : undefined;

      if (Number.isFinite(fallbackId)) {
        history.replace(`/groups/${group_id}/maps/${fallbackId}/traps`);
      } else {
        history.replace(`/traps/${group_id}`);
      }
    } finally {
      setMapActionPending(false);
      setMapActionType(null);
    }
  }, [deleteGroupMapAction, deleteModal, group_id, history, isMapActionPending, isSystemManagedGroup, map, maps, modalMapId, mutateMap, mutateMaps, data, mutate, updateTrapAction]);

  useEffect(() => {
    if (!group_id || !maps.length) {
      return;
    }

    const hasSelectedMap = maps.some(({ id }) => Number(id) === map_id);

    if (!hasSelectedMap) {
      const fallbackId = maps[0].id;
      const normalizedFallbackId = Number(fallbackId);

      if (Number.isFinite(normalizedFallbackId)) {
        history.replace(`/groups/${group_id}/maps/${normalizedFallbackId}/traps`);
      }
    }
  }, [group_id, history, map_id, maps]);

  return (
    <>
      <div>
        <WorkspaceBanner />

        <LargeContainer className="mt-4">
          <SimpleHeader className="mt-0 mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <Breadcrumb className="mb-0">
                <Breadcrumb.Item
                  linkAs={RouterLink}
                  linkProps={{ to: '/traps', style: { textDecoration: 'none' }, onClick: handleHomeClick }}
                >
                  <i className="bi bi-house-fill" aria-hidden="true" /><span className="visually-hidden">{__('Trap Groups')}</span>
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  linkAs={RouterLink}
                  linkProps={{ to: `/traps/${group_id}`, style: { textDecoration: 'none' }, onClick: handleGroupClick }}
                >
                  {groupName}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{__('Building Maps')}</Breadcrumb.Item>
              </Breadcrumb>

              <div className="d-flex align-items-end gap-2">
                <div className="d-flex align-items-end gap-2">
                  <Form.Select
                    value={Number.isFinite(map_id) ? String(map_id) : ''}
                    onChange={(e) => handleMapChange(e.target.value)}
                    disabled={!Array.isArray(maps) || maps.length === 0}
                    aria-label={__('Select map')}
                    style={{ minWidth: 180 }}
                  >
                    {(!Array.isArray(maps) || maps.length === 0) && <option value="">{__('No maps available')}</option>}
                    {Array.isArray(maps) && maps.map(({ id, name }) => (
                      <option key={id} value={String(id)}>{name}</option>
                    ))}
                  </Form.Select>
                  {!isSystemManagedGroup && canEditGroupMaps && (
                    <TooltipControl tooltip={__('Edit Map')} placement="top">
                      <Button
                        variant="outline-primary"
                        onClick={handleOpenMapModal}
                        disabled={!canEditTrapPositions}
                        className="d-inline-flex align-items-center justify-content-center"
                        aria-label={__('Edit Map')}>
                          <i className="bi bi-pencil bi-x1-5" aria-hidden="true" />
                          <span className="visually-hidden">{__('Edit Map')}</span>
                      </Button>
                    </TooltipControl>
                  )}
                </div>

                {!isSystemManagedGroup && canEditGroupMaps && (
                  <TooltipControl tooltip={Array.isArray(maps) && maps.length >= MAX_MAP_PER_GROUP ? __('Max limit reached') : __('New Map')} placement="top">
                    <Button 
                      variant={!Number.isFinite(group_id) || (Array.isArray(maps) && maps.length >= MAX_MAP_PER_GROUP) ? "outline-danger" : "outline-primary"} 
                      onClick={handleNewMapClick} 
                      className="d-inline-flex align-items-center justify-content-center" 
                      aria-label={__('New Map')}
                    >
                      <i className="bi bi-plus bi-x2" aria-hidden="true" />
                      <span className="visually-hidden">{__('New Map')}</span>
                    </Button>
                  </TooltipControl>
                )}
              </div>
            </div>
          </SimpleHeader>
        </LargeContainer>

        <div className={cls('d-flex flex-row flex-grow-1', classnames.root)}>

          <div className={cls(classnames.page, { 'd-none': minimized })}>
            <BuildingMapsSidePanel items={visibleItems}
              selectedItem={selectedItem} onSelectItem={handleSelectItem}
              editMode={editMode} onEditMode={handleEditModeChange} onDropItem={handleDropItem}
              currentMapId={map_id}
              isValidBuildingPosition={isValidBuildingPosition} mapOptions={maps}
              onSelectMap={handleMapChange}
              canToggleEditMode={canToggleEditMode}
              canEditGroupMaps={canEditGroupMaps}
              groupId={group_id}
              availableGroupIds={availableGroupIds}
            />
          </div>

          <VerticalSplitter onClick={() => setMinimized(!minimized)} />

          <BuildingMapsPanel items={items} editMode={editMode} onCenterChanged={setCenter}
            selectedItem={selectedItem} onSelectItem={handleSelectItem} map={map}
            isValidBuildingPosition={isValidBuildingPosition} onRemoveFromMap={handleRemoveFromMap}
            onItemPositionChange={async (item) => {
              const pos = item.changed;
              if (!canEditTrapPositions) { return; }
              await updateTrapAction({ trap_id: item.trap_id, map_id, x: pos?.x ?? '', y: pos?.y ?? '' });
            }}
          />

        </div>

      </div>

      <Modal show={isMapModalVisible} onHide={handleCloseMapModal} centered size="md" backdrop="static">
        <Form onSubmit={handleSaveMapDetails}>
          <Modal.Header closeButton={!isMapActionPending}>
            <Modal.Title>{mapModalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="map-edit-name">
              <FloatingLabel label={__('Map Name')}>
                <Form.Control
                  type="text"
                  value={mapFormName}
                  onChange={handleMapFormNameChange}
                  placeholder={__('Map Name')}
                  maxLength={40}
                  isInvalid={!!mapFormError}
                  disabled={isMapActionPending}
                />
              </FloatingLabel>
              {mapFormError && <Form.Control.Feedback type="invalid" className="d-block">{mapFormError}</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group controlId="map-edit-image" className="mb-0">
              <Form.Label>{__('Map Image')}</Form.Label>
              <ImageFileInput
                key={mapImageInputKey}
                accept={'image/*'}
                maxSize={MAX_MAP_IMAGE_SIZE}
                onImageSelect={handleMapFormImageSelect}
              />
              <Form.Text className="text-muted">{__('Supported formats: JPG, PNG, GIF. Maximum file size: 5MB.')}</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="d-flex align-items-center">
            {isEditingMap && (
              <Button variant="outline-danger" onClick={handleDeleteMap} disabled={isMapActionPending}>
                {mapActionType === 'delete' ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                {__('Delete Map')}
              </Button>
            )}
            <div className="ms-auto d-flex gap-2">
              <Button variant="secondary" onClick={handleCloseMapModal} disabled={isMapActionPending}>
                {__('Cancel')}
              </Button>
              <Button type="submit" disabled={isMapActionPending}>
                {mapActionType === 'save' && <Spinner animation="border" size="sm" className="me-2" />}<span>{mapPrimaryActionLabel}</span>
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      <ConfirmModalDialog {...deleteModal.register()} />
      <ConfirmModalDialog {...buildingMapWarningModal.register()} />
    </>
  )
})
